import { styled } from "@linaria/react";
import { Icon as PhosphorIcon } from "@phosphor-icons/react";

import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import Spacing from "@gx/dls/containers/Spacing";
import Icon, { IconVariant } from "@gx/dls/icons/Icon";
import Text, { TextSize, TextVariant, TextWeight } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";

const ErrorLayoutWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;
  padding: 20px;

  box-sizing: border-box;
`);

const ErrorDetailsWrapper = withTheme(styled.div<PropsWithTheme>`
  min-width: 480px;
  max-width: 720px;
  width: 100%;
  padding: 16px;

  background-color: ${({ theme }) => theme.color.background.secondary};
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};
  border-radius: 5px;

  overflow-x: scroll;
  white-space: pre-wrap;
  word-break: break-word;
`);

interface ErrorLayoutProps {
  icon: PhosphorIcon;
  header: string;
  message: string;
  error?: Error;
  actions?: React.ReactNode;
}

const ErrorLayout = ({ icon, header, message, error, actions }: ErrorLayoutProps) => {
  return (
    <ErrorLayoutWrapper>
      <FlexWrapper
        direction={FlexDirection.COLUMN}
        justifyContent={JustifyContent.CENTER}
        alignItems={AlignItems.CENTER}
      >
        <Spacing bottom={12}>
          <Icon component={icon} variant={IconVariant.ERROR} size={24} />
        </Spacing>
        <Spacing bottom={8}>
          <Text size={TextSize.BODY_LG}>{header}</Text>
        </Spacing>

        <Text variant={TextVariant.TERTIARY}>{message}</Text>
      </FlexWrapper>
      {error && (
        <Spacing top={20}>
          <ErrorDetailsWrapper>
            {error && (
              <FlexWrapper direction={FlexDirection.COLUMN} gap={8}>
                <FlexWrapper direction={FlexDirection.COLUMN} gap={6}>
                  <Text variant={TextVariant.ERROR}>Error</Text>
                  <Text variant={TextVariant.SECONDARY} isSelectable>
                    {error.message}
                  </Text>
                </FlexWrapper>

                {error.stack && (
                  <Text variant={TextVariant.TERTIARY} weight={TextWeight.REGULAR} isSelectable>
                    {error.stack}
                  </Text>
                )}
              </FlexWrapper>
            )}
          </ErrorDetailsWrapper>
        </Spacing>
      )}
      <Spacing top={16} />
      {actions}
    </ErrorLayoutWrapper>
  );
};

export default ErrorLayout;
